import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DoneIcon from "@mui/icons-material/Done";
import DownloadIcon from "@mui/icons-material/Download";
import IosShareIcon from "@mui/icons-material/IosShare";
import SelectAllIcon from "@mui/icons-material/SelectAll";
import DeselectIcon from "@mui/icons-material/Deselect";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { CircularProgress, Dialog, Modal } from "@mui/material";
import { collection, getDocs, Timestamp } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { getBlob, ref } from "firebase/storage";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { db, functions, storage } from "../../firebase";
import { getCachedImage, putCachedImage } from "../../utils/galleryImageCache";
import styles from "./photos.module.css";

interface GalleryCollection {
  id: string;
  title: string;
  order: number;
  photoCount?: number;
}

interface GalleryPhoto {
  id: string;
  collectionId: string;
  fileName: string;
  order: number;
  // Small WebP for the grid and a tiny blurred data URL, added by the upload script's
  // --new-sizes-only pass. Older records fall back to thumbPath / no placeholder.
  gridPath?: string;
  placeholder?: string;
  thumbPath: string;
  displayPath: string;
  width: number;
  height: number;
  hidden?: boolean;
  uploadedAt?: Timestamp;
}

const logPhotoDownload = httpsCallable<{ photoId: string }, { logged: boolean }>(functions, "logPhotoDownload");
const updateGalleryPhotos = httpsCallable<{ photoIds: string[]; action: PhotoAction }, { updatedIds: string[] }>(
  functions,
  "updateGalleryPhotos"
);

type PhotoAction = "hide" | "unhide";

const byOrder = (a: GalleryPhoto, b: GalleryPhoto) => a.order - b.order;

// Before a guest's first download we ask them to confirm they won't print photos as gifts.
// Remembered per browser; if storage is unavailable they're simply asked again next time.
const PRINT_NOTE_KEY = "galleryPrintNoteAccepted";

const hasAcceptedPrintNote = () => {
  try {
    return localStorage.getItem(PRINT_NOTE_KEY) === "true";
  } catch {
    return false;
  }
};

const rememberPrintNoteAccepted = () => {
  try {
    localStorage.setItem(PRINT_NOTE_KEY, "true");
  } catch {
    // Storage unavailable: they'll see the note again next time.
  }
};

/**
 * Loads a Storage object through the SDK (so Storage rules are enforced on every
 * request) and returns an object URL. Nothing public or long-lived is handed out.
 * Images already seen this session come from the browser cache instead.
 * If `path` fails to load and a `fallbackPath` is given, that is tried instead.
 */
const useStorageImage = (path: string | null, version?: number, fallbackPath?: string) => {
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    if (!path) return;
    let objectUrl: string | null = null;
    let cancelled = false;

    const fetchImage = async (imagePath: string) => {
      let blob = await getCachedImage(imagePath, version);
      if (!blob) {
        blob = await getBlob(ref(storage, imagePath));
        putCachedImage(imagePath, version, blob);
      }
      return blob;
    };

    const load = async () => {
      let blob: Blob;
      try {
        blob = await fetchImage(path);
      } catch (error) {
        if (!fallbackPath || fallbackPath === path) throw error;
        blob = await fetchImage(fallbackPath);
      }
      if (cancelled) return;
      objectUrl = URL.createObjectURL(blob);
      setSrc(objectUrl);
    };

    load().catch((error) => console.error("Failed to load photo", path, error));

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setSrc(null);
    };
  }, [path, version, fallbackPath]);

  return src;
};

const Thumbnail: React.FC<{
  photo: GalleryPhoto;
  selectMode: boolean;
  selected: boolean;
  onClick: (event: React.MouseEvent) => void;
}> = ({ photo, selectMode, selected, onClick }) => {
  const containerRef = useRef<HTMLButtonElement>(null);
  const [visible, setVisible] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const src = useStorageImage(
    visible ? photo.gridPath || photo.thumbPath : null,
    photo.uploadedAt?.toMillis(),
    photo.thumbPath
  );

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "300px" }
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <button
      ref={containerRef}
      className={`${styles.thumb} ${selectMode ? styles.thumbSelectable : ""} ${selected ? styles.thumbSelected : ""} ${
        photo.hidden ? styles.thumbHidden : ""
      }`}
      style={{
        aspectRatio: `${photo.width} / ${photo.height}`,
        // Justified rows: each photo's share of the row is proportional to its aspect ratio.
        flexGrow: photo.width / photo.height,
        flexBasis: `calc(var(--row-height) * ${photo.width / photo.height})`,
      }}
      onClick={onClick}
      aria-label={selectMode ? `${selected ? "Deselect" : "Select"} ${photo.fileName}` : `Open ${photo.fileName}`}
      aria-pressed={selectMode ? selected : undefined}
    >
      {/* Blurred placeholder shows immediately; the real image fades in over it. */}
      {photo.placeholder && !loaded && (
        <img className={styles.thumbPlaceholder} src={photo.placeholder} alt="" aria-hidden="true" />
      )}
      {src && (
        <img
          className={`${styles.thumbImage} ${loaded ? styles.thumbImageLoaded : ""}`}
          src={src}
          alt=""
          onLoad={() => setLoaded(true)}
        />
      )}
      {photo.hidden && <span className={styles.hiddenBadge}>Hidden</span>}
      {selectMode && <span className={styles.selectMark} aria-hidden="true">{selected ? "✓" : ""}</span>}
    </button>
  );
};

const Lightbox: React.FC<{
  photos: GalleryPhoto[];
  index: number;
  onChange: (index: number) => void;
  onClose: () => void;
}> = ({ photos, index, onChange, onClose }) => {
  const { isAdmin } = useAuth();
  const photo = photos[index];
  const [showPrintNote, setShowPrintNote] = useState(false);
  const [printNoteChecked, setPrintNoteChecked] = useState(false);
  const version = photo.uploadedAt?.toMillis();
  // The thumbnail is almost always already cached from the grid, so show it straight
  // away (scaled up) with a spinner until the sharper display image arrives.
  const thumbSrc = useStorageImage(photo.thumbPath, version);
  const displaySrc = useStorageImage(photo.displayPath, version);
  const src = displaySrc || thumbSrc;
  const [message, setMessage] = useState("");

  const goTo = useCallback(
    (next: number) => {
      setMessage("");
      onChange((next + photos.length) % photos.length);
    },
    [onChange, photos.length]
  );

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (showPrintNote) return;
      if (event.key === "ArrowLeft") goTo(index - 1);
      if (event.key === "ArrowRight") goTo(index + 1);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [goTo, index, showPrintNote]);

  // Saves the display-size image the lightbox already has loaded, as a normal browser
  // download (no new request, no signed link). The log call is fire-and-forget.
  const saveImage = () => {
    if (!displaySrc) return;
    const link = document.createElement("a");
    link.href = displaySrc;
    link.download = photo.fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    logPhotoDownload({ photoId: photo.id }).catch((error) => console.error("Failed to log download", error));
  };

  const handleDownload = () => {
    if (!displaySrc) return;
    if (!isAdmin && !hasAcceptedPrintNote()) {
      setPrintNoteChecked(false);
      setShowPrintNote(true);
      return;
    }
    saveImage();
  };

  const acceptPrintNoteAndDownload = () => {
    rememberPrintNoteAccepted();
    setShowPrintNote(false);
    saveImage();
  };

  // Shares a link that opens /photos with this photo in the lightbox. Recipients who
  // aren't signed in go through the normal guest sign-in first.
  const handleShare = async () => {
    setMessage("");
    const url = `${window.location.origin}/photos?photo=${encodeURIComponent(photo.id)}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Alexandria & Morgan's wedding", text: "A photo from Alexandria & Morgan's wedding", url });
        return;
      } catch (error) {
        if ((error as DOMException)?.name === "AbortError") return; // Share sheet dismissed.
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setMessage("Link copied");
    } catch {
      setMessage(url); // Clipboard blocked: show the link so it can be copied by hand.
    }
  };

  return (
    <Modal open onClose={onClose}>
      <div className={styles.lightbox}>
        <button className={styles.lightboxClose} onClick={onClose} aria-label="Close">
          <CloseIcon />
        </button>
        <button className={`${styles.lightboxNav} ${styles.prev}`} onClick={() => goTo(index - 1)} aria-label="Previous photo">
          <ChevronLeftIcon fontSize="large" />
        </button>
        {/* Sized from the photo's aspect ratio so swapping thumbnail -> display doesn't jump. */}
        <div
          className={styles.lightboxImage}
          style={{ "--photo-ratio": photo.width / photo.height } as React.CSSProperties}
        >
          {src && <img src={src} alt="" />}
          {!displaySrc && (
            <div className={styles.lightboxSpinner} role="status" aria-label="Loading full-size photo">
              <CircularProgress size={44} thickness={4} sx={{ color: "white" }} />
            </div>
          )}
        </div>
        <button className={`${styles.lightboxNav} ${styles.next}`} onClick={() => goTo(index + 1)} aria-label="Next photo">
          <ChevronRightIcon fontSize="large" />
        </button>
        <div className={styles.lightboxFooter}>
          <span>
            {index + 1} / {photos.length}
          </span>
          <button className={styles.downloadButton} onClick={handleDownload} disabled={!displaySrc}>
            <DownloadIcon fontSize="small" />
            {displaySrc ? "Download" : "Loading..."}
          </button>
          <button className={styles.downloadButton} onClick={handleShare}>
            <IosShareIcon fontSize="small" />
            Share
          </button>
          {message && <span className={styles.lightboxMessage}>{message}</span>}
        </div>

        <Dialog
          open={showPrintNote}
          onClose={() => setShowPrintNote(false)}
          aria-labelledby="print-note-title"
          PaperProps={{ className: styles.printNote }}
        >
          <h2 id="print-note-title" className={styles.printNoteTitle}>
            Before you download
          </h2>
          <p>
            We're so glad you want to keep these! Just a quick reminder: we've already printed the photos we
            want for ourselves, so please don't print any as gifts for us. No prints, frames, canvases, mugs
            or photo books needed.
          </p>
          <label className={styles.printNoteCheck}>
            <input
              type="checkbox"
              checked={printNoteChecked}
              onChange={(e) => setPrintNoteChecked(e.target.checked)}
            />
            <span>Got it, I won't print any photos as gifts for Alexandria &amp; Morgan</span>
          </label>
          <div className={styles.printNoteActions}>
            <button className="accent-btn" onClick={() => setShowPrintNote(false)}>
              Cancel
            </button>
            <button className="primary-btn" onClick={acceptPrintNoteAndDownload} disabled={!printNoteChecked}>
              Download
            </button>
          </div>
        </Dialog>
      </div>
    </Modal>
  );
};

const Gallery: React.FC = () => {
  const { isGuest, isAdmin, guestName, signOut } = useAuth();
  const [collections, setCollections] = useState<GalleryCollection[]>([]);
  const [activeCollectionId, setActiveCollectionId] = useState<string | null>(null);
  const [visiblePhotos, setVisiblePhotos] = useState<GalleryPhoto[]>([]);
  const [hiddenPhotos, setHiddenPhotos] = useState<GalleryPhoto[]>([]);
  const [sharedNotice, setSharedNotice] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Admin tools
  const [showHidden, setShowHidden] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [lastClickedIndex, setLastClickedIndex] = useState<number | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [toolbarMessage, setToolbarMessage] = useState("");
  const [hiddenUnavailable, setHiddenUnavailable] = useState(false);

  // All photos for every collection are loaded up front; tabs just filter.
  const photos = useMemo(() => {
    const inCollection = (p: GalleryPhoto) => p.collectionId === activeCollectionId;
    const visible = visiblePhotos.filter(inCollection);
    return showHidden ? [...visible, ...hiddenPhotos.filter(inCollection)].sort(byOrder) : visible;
  }, [activeCollectionId, showHidden, visiblePhotos, hiddenPhotos]);

  // The open photo lives in the URL (?photo=<id>) so the address bar always points at the
  // photo being viewed and shared links open straight into the lightbox.
  const [searchParams, setSearchParams] = useSearchParams();
  const openPhotoId = searchParams.get("photo");
  const openIndex = openPhotoId ? photos.findIndex((p) => p.id === openPhotoId) : -1;

  const setOpenPhotoId = useCallback(
    (id: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (id) next.set("photo", id);
          else next.delete("photo");
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // Once photos load, point the gallery at a shared photo: switch to its tab (and, for
  // admins, show hidden photos if it's hidden). Runs once per page load.
  const sharedLinkResolved = useRef(false);
  useEffect(() => {
    if (loading || sharedLinkResolved.current) return;
    sharedLinkResolved.current = true;
    if (!openPhotoId) return;

    const shared = visiblePhotos.find((p) => p.id === openPhotoId) || hiddenPhotos.find((p) => p.id === openPhotoId);
    if (!shared) {
      setSharedNotice("The shared photo is no longer available, but you can browse all the others below.");
      setOpenPhotoId(null);
      return;
    }
    setActiveCollectionId(shared.collectionId);
    if (shared.hidden) setShowHidden(true);
  }, [loading, openPhotoId, visiblePhotos, hiddenPhotos, setOpenPhotoId]);

  // Collections and photos are fetched in parallel (rather than collections, then the
  // first collection's photos) to save a round trip before thumbnails can start.
  useEffect(() => {
    const loadPhotos = async (collectionName: string, hidden: boolean) => {
      const snapshot = await getDocs(collection(db, collectionName));
      return snapshot.docs.map((d) => ({ ...(d.data() as GalleryPhoto), id: d.id, hidden })).sort(byOrder);
    };

    // Hidden photos are admin-only; guests can't read that collection at all. If they
    // fail to load, still show the visible photos rather than failing the whole page.
    const loadHidden = isAdmin
      ? loadPhotos("galleryHiddenPhotos", true).catch((err) => {
          console.error("Failed to load hidden photos", err);
          setHiddenUnavailable(true);
          return [] as GalleryPhoto[];
        })
      : Promise.resolve([] as GalleryPhoto[]);

    setLoading(true);
    setHiddenUnavailable(false);
    Promise.all([getDocs(collection(db, "galleryCollections")), loadPhotos("galleryPhotos", false), loadHidden])
      .then(([collectionsSnapshot, visible, hidden]) => {
        const items = collectionsSnapshot.docs
          .map((d) => ({ id: d.id, ...d.data() } as GalleryCollection))
          .sort((a, b) => a.order - b.order);
        setCollections(items);
        setActiveCollectionId((current) => current ?? items[0]?.id ?? null);
        setVisiblePhotos(visible);
        setHiddenPhotos(hidden);
      })
      .catch((err) => {
        console.error("Failed to load gallery", err);
        setError("We couldn't load the gallery. Please refresh the page or sign in again.");
      })
      .finally(() => setLoading(false));
  }, [isAdmin]);

  // Switching tabs clears any selection.
  useEffect(() => {
    setSelectedIds(new Set());
    setLastClickedIndex(null);
  }, [activeCollectionId]);

  const handleThumbClick = (index: number, event: React.MouseEvent) => {
    if (!selectMode) {
      setOpenPhotoId(photos[index].id);
      return;
    }

    const clickedId = photos[index].id;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (event.shiftKey && lastClickedIndex !== null) {
        // Shift-click selects everything between the last click and this one.
        const [start, end] = [Math.min(lastClickedIndex, index), Math.max(lastClickedIndex, index)];
        photos.slice(start, end + 1).forEach((p) => next.add(p.id));
      } else if (next.has(clickedId)) {
        next.delete(clickedId);
      } else {
        next.add(clickedId);
      }
      return next;
    });
    setLastClickedIndex(index);
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setLastClickedIndex(null);
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    clearSelection();
  };

  const selectedVisible = visiblePhotos.filter((p) => selectedIds.has(p.id));
  const selectedHidden = hiddenPhotos.filter((p) => selectedIds.has(p.id));

  const runAction = async (action: PhotoAction) => {
    const targets = action === "hide" ? selectedVisible : selectedHidden;
    if (targets.length === 0) return;

    setActionBusy(true);
    setToolbarMessage("");
    try {
      const result = await updateGalleryPhotos({ photoIds: targets.map((p) => p.id), action });
      const moved = new Set(result.data.updatedIds);
      const movedPhotos = targets.filter((p) => moved.has(p.id)).map((p) => ({ ...p, hidden: action === "hide" }));

      if (action === "hide") {
        setVisiblePhotos((prev) => prev.filter((p) => !moved.has(p.id)));
        setHiddenPhotos((prev) => [...prev, ...movedPhotos].sort(byOrder));
      } else {
        setHiddenPhotos((prev) => prev.filter((p) => !moved.has(p.id)));
        setVisiblePhotos((prev) => [...prev, ...movedPhotos].sort(byOrder));
      }

      clearSelection();
      setToolbarMessage(`${moved.size} photo${moved.size === 1 ? "" : "s"} ${action === "hide" ? "hidden" : "unhidden"}`);
    } catch (err) {
      console.error(`Failed to ${action} photos`, err);
      setToolbarMessage((err as { message?: string })?.message || `Couldn't ${action} photos. Please try again.`);
    } finally {
      setActionBusy(false);
    }
  };

  return (
    <div className={`${styles.gallery} ${isAdmin ? styles.galleryWithToolbar : ""}`}>
      {isGuest && (
        <div className={styles.sessionBar}>
          <span>{guestName ? `Signed in as ${guestName}` : "Signed in"}</span>
          <button className={styles.linkButton} onClick={signOut}>
            Sign out
          </button>
        </div>
      )}

      {collections.length > 1 && (
        <div className={styles.tabs}>
          {collections.map((c) => (
            <button
              key={c.id}
              className={`${styles.tab} ${c.id === activeCollectionId ? styles.tabActive : ""}`}
              onClick={() => {
                setActiveCollectionId(c.id);
                setOpenPhotoId(null);
              }}
            >
              {c.title}
            </button>
          ))}
        </div>
      )}

      {error && <div className={styles.error}>{error}</div>}
      {sharedNotice && <div className={styles.notice}>{sharedNotice}</div>}
      {loading && <p className={styles.status}>Loading photos...</p>}
      {!loading && !error && photos.length === 0 && (
        <p className={styles.status}>The photos are on their way. Check back soon!</p>
      )}

      {!loading && (
        <div className={styles.grid}>
          {photos.map((photo, i) => (
            <Thumbnail
              key={photo.id}
              photo={photo}
              selectMode={selectMode}
              selected={selectedIds.has(photo.id)}
              onClick={(event) => handleThumbClick(i, event)}
            />
          ))}
        </div>
      )}

      {openIndex >= 0 && (
        <Lightbox
          photos={photos}
          index={openIndex}
          onChange={(index) => setOpenPhotoId(photos[index].id)}
          onClose={() => setOpenPhotoId(null)}
        />
      )}

      {isAdmin && (
        <div className={styles.dockWrap}>
          {(actionBusy || toolbarMessage || hiddenUnavailable) && (
            <div className={styles.dockMessage} role="status">
              {actionBusy
                ? "Saving..."
                : toolbarMessage || "Hidden photos couldn't be loaded. Deploy the latest Firestore rules."}
            </div>
          )}
          <div className={styles.dock} role="toolbar" aria-label="Admin photo tools">
            {selectMode ? (
              <>
                <span className={styles.dockCount}>{selectedIds.size} selected</span>
                <span className={styles.dockDivider} />
                <button className={styles.dockItem} onClick={() => setSelectedIds(new Set(photos.map((p) => p.id)))}>
                  <SelectAllIcon fontSize="small" />
                  <span>Select all</span>
                </button>
                <button className={styles.dockItem} onClick={clearSelection} disabled={selectedIds.size === 0}>
                  <DeselectIcon fontSize="small" />
                  <span>Clear</span>
                </button>
                {(selectedVisible.length > 0 || selectedHidden.length > 0) && <span className={styles.dockDivider} />}
                {selectedVisible.length > 0 && (
                  <button
                    className={`${styles.dockItem} ${styles.dockPrimary}`}
                    onClick={() => runAction("hide")}
                    disabled={actionBusy}
                  >
                    <VisibilityOffIcon fontSize="small" />
                    <span>Hide {selectedVisible.length}</span>
                  </button>
                )}
                {selectedHidden.length > 0 && (
                  <button
                    className={`${styles.dockItem} ${styles.dockPrimary}`}
                    onClick={() => runAction("unhide")}
                    disabled={actionBusy}
                  >
                    <VisibilityIcon fontSize="small" />
                    <span>Unhide {selectedHidden.length}</span>
                  </button>
                )}
                <span className={styles.dockDivider} />
                <button className={styles.dockItem} onClick={exitSelectMode}>
                  <DoneIcon fontSize="small" />
                  <span>Done</span>
                </button>
              </>
            ) : (
              <button className={styles.dockItem} onClick={() => setSelectMode(true)}>
                <CheckCircleOutlineIcon fontSize="small" />
                <span>Select</span>
              </button>
            )}
            <span className={styles.dockDivider} />
            <label className={`${styles.dockItem} ${styles.dockSwitch}`} aria-disabled={hiddenUnavailable}>
              <input
                type="checkbox"
                role="switch"
                checked={showHidden}
                disabled={hiddenUnavailable}
                onChange={(e) => {
                  setShowHidden(e.target.checked);
                  setLastClickedIndex(null);
                }}
              />
              <span className={styles.switchTrack} aria-hidden="true" />
              <span>Show hidden ({hiddenPhotos.length})</span>
            </label>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
